import type { HTMLComponent } from "@commercekit/react";
import { useLocationSearch } from "@commercekit/react";
import type {
  ProductAttributeSearchFilter,
  WovarCategory,
  WovarCustomer,
  WovarProduct,
} from "@commercekit/api";

import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { useNavigate } from "@remix-run/react";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ProductAttributeSearchFilterGrouped,
  ProductSearchFilterGroupedOption,
} from "@commercekit/remix";
import { categoryFilter } from "@commercekit/remix";
import { CKCollapsible, CKFilter } from "@commercekit/ui";
import WbFilterGroup from "../molecules/WvFilterGroup";
import WvProductItem from "../molecules/WvProductItem";
import { AnimatePresence, motion } from "framer-motion";
import { SliceZoneC } from "@commercekit/prismic";
import type { UspBarImageAndTextSlice, RichTextSlice, FaqSliceSlice } from "prismicio-types";
import { UspBarImageAndText, RichText, FaqSection } from "~/lib/prismic";
import WvProductsSortingCard from "../molecules/WvProductsSortingCard";
import { fixRelativeLinks, wrapIframe } from "@commercekit/shared";
import WvMultiSelect from "../molecules/WvMultiSelect";
import WvProductListPagination from "../molecules/WvProductListPagination";
import { formatFilterValue } from "~/lib/prismic/utils";

const { str2obj, checkedOptions2str } = categoryFilter();

const FILTERS_GAP = 16;
const MOBILE_FILTERS_MEDIA_QUERY = "(max-width: 991.98px)";
const PRODUCTS_PER_ROW = 6;
const EAGER_IMAGE_COUNT = 4;

const WvSearchFilter: HTMLComponent<{
  filters: ProductAttributeSearchFilter[];
  quickMobileFilters: ProductAttributeSearchFilter;
  isProductLoading: boolean;
  products: WovarProduct[];
  totalProductCount: number;
  uspBarImageAndTextSlices: UspBarImageAndTextSlice[];
  richTextSlices: RichTextSlice[];
  faqSlice: FaqSliceSlice[];
  categoryData?: WovarCategory;
  loadRef: React.RefObject<HTMLDivElement>;
  customer?: WovarCustomer;
  showTotalCount?: boolean;
  showDescriptions?: boolean;
  /** WOV-2195: numbered page links instead of the infinite-scroll sentinel. */
  usePagination?: boolean;
  currentPage?: number;
  /** Set on the PLP only; base category pages show subcategories above the grid. */
  prioritizeFirstImages?: boolean;
}> = ({
  filters = [],
  quickMobileFilters,
  isProductLoading,
  products = [],
  totalProductCount,
  uspBarImageAndTextSlices,
  richTextSlices,
  faqSlice,
  categoryData,
  loadRef,
  customer,
  showTotalCount = false,
  showDescriptions = true,
  usePagination = false,
  currentPage = 1,
  prioritizeFirstImages = false,
}) => {
  const { $pt } = usePrismicLabels();
  const { search, linkTo } = useLocationSearch();
  const navigate = useNavigate();
  const active = useMemo(() => str2obj(search), [search]);
  const activeRef = useRef(0);
  useEffect(() => {
    activeRef.current += 1;
  }, []);

  // keeps the desktop filter panel's bottom inside the viewport
  const syncFiltersViewport = useCallback(() => {
    const el = document.getElementById("openCloseMobileFilter");
    if (!el) return;

    if (window.innerWidth < 992) {
      el.style.removeProperty("--filters-sticky-top");
      el.style.removeProperty("--filters-max-height");
      return;
    }

    const header = document.querySelector("header.wvheader");
    const headerBottom = header
      ? Math.max(header.getBoundingClientRect().bottom, 0)
      : 0;
    const stickyTop = headerBottom + FILTERS_GAP;
    const top = Math.max(el.getBoundingClientRect().top, stickyTop);
    const pinnedHeight = Math.max(
      window.innerHeight - stickyTop - FILTERS_GAP,
      0
    );
    const fittedHeight = window.innerHeight - top - FILTERS_GAP;

    el.style.setProperty("--filters-sticky-top", `${stickyTop}px`);
    el.style.setProperty(
      "--filters-max-height",
      `${fittedHeight > 0 ? fittedHeight : pinnedHeight}px`
    );
  }, []);

  // no deps: CKFilter's key remounts the form
  useEffect(() => {
    syncFiltersViewport();
  });

  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        syncFiltersViewport();
      });
    };

    // capture: body scrolls, and scroll does not bubble
    window.addEventListener("scroll", schedule, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", schedule);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
    };
  }, [syncFiltersViewport]);

  const productsPerRow: WovarProduct[][] = [];
  for (let i = 0, j = products.length; i < j; i += PRODUCTS_PER_ROW) {
    productsPerRow.push(products.slice(i, i + PRODUCTS_PER_ROW));
  }
  const imagePriorityAt = (position: number) => {
    if (!prioritizeFirstImages) return undefined;
    if (position === 0) return "high";
    return position < EAGER_IMAGE_COUNT ? "eager" : undefined;
  };
  // wrap options of filters with group name
  const filtersGrouped: ProductAttributeSearchFilterGrouped[] = useMemo(() => {
    return filters
      .filter((i) => i.name && i.options && i.options.length)
      .map(({ name, options }) => ({
        name,
        type: "checkbox",
        options: options.map((o) => ({
          ...o,
          groupName: o.key,
          type: "checkbox",
        })),
      }));
  }, [filters]);

  const defaultValue = useMemo(() => {
    return filtersGrouped.reduce(
      (prev: ProductSearchFilterGroupedOption[], { name, options }) => {
        if (active[name]) {
          return [...prev, ...options.filter((o) => active[name][o.value])];
        }
        return prev;
      },
      []
    );
  }, [active, filtersGrouped]);

  const onChange = useCallback(
    (checkedOptions: ProductSearchFilterGroupedOption[]) => {
      if (isProductLoading) return;
      // WOV-2195: a different filter is a different result set, so drop ?page=N.
      navigate(linkTo({ filter: checkedOptions2str(checkedOptions), page: "" }));
    },
    [isProductLoading, linkTo, navigate]
  );

  const identity = useCallback(
    (item: ProductSearchFilterGroupedOption) => `${item.name}_${item.value}`,
    []
  );

  const openMobileFilters = () => {
    let d = document.getElementById("openCloseMobileFilter");
    if (d) {
      d.setAttribute("data-open", "");
    }
  };

  const closeMobileFilters = () => {
    let d = document.getElementById("openCloseMobileFilter");
    if (d) {
      d.removeAttribute("data-open");
    }
  };

  const toggleSelectedFilters = (open: boolean) => {
    let d = document.getElementById("selectedFiltersToggle");
    if (d) {
      open ? d.setAttribute("data-open", "") : d.removeAttribute("data-open");
    }
  };

  const [useMobileFilters, setUseMobileFilters] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_FILTERS_MEDIA_QUERY);
    const syncFilterLayout = () => setUseMobileFilters(mediaQuery.matches);

    syncFilterLayout();
    mediaQuery.addEventListener
      ? mediaQuery.addEventListener("change", syncFilterLayout)
      : mediaQuery.addListener(syncFilterLayout);

    return () =>
      mediaQuery.removeEventListener
        ? mediaQuery.removeEventListener("change", syncFilterLayout)
        : mediaQuery.removeListener(syncFilterLayout);
  }, []);

  const descriptions = useMemo(() => {
    return {
      long: fixRelativeLinks(wrapIframe(categoryData?.longDescription)),
      short: fixRelativeLinks(wrapIframe(categoryData?.shortDescription)),
    };
  }, [categoryData?.longDescription, categoryData?.shortDescription]);

  // Get the "mobile quick filter" filter group for creating filter options
  const quickFilterGroup = useMemo(() => {
    return filtersGrouped.find(
      (filter) => filter.name === quickMobileFilters.name
    );
  }, [filtersGrouped, quickMobileFilters.name]);

  // Get currently selected values from defaultValue
  const selectedValues = useMemo(() => {
    return defaultValue
      .filter((option) => option.name === quickMobileFilters.name)
      .map((option) => option.value);
  }, [defaultValue, quickMobileFilters.name]);

  // Handle mobile quick filter selection changes from WvMultiSelect
  const handleSelectionChange = useCallback(
    (selectedValues: string[]) => {
      if (isProductLoading || !quickFilterGroup) return;

      // Convert selected mobile quick filter values to ProductSearchFilterGroupedOption format
      const mobileQuickFilterOptions: ProductSearchFilterGroupedOption[] =
        selectedValues
          .map((value) => {
            const option = quickFilterGroup.options.find(
              (opt) => opt.value === value
            );
            return option ? { ...option } : null;
          })
          .filter(
            (opt): opt is ProductSearchFilterGroupedOption => opt !== null
          );

      // Get all other filter options (excluding "mobile quick filter")
      const otherOptions = defaultValue.filter(
        (option) => option.name !== quickMobileFilters.name
      );

      // Merge mobile quick filter options with other options
      const mergedOptions = [...otherOptions, ...mobileQuickFilterOptions];

      // Call onChange with merged options
      onChange(mergedOptions);
    },
    [isProductLoading, quickFilterGroup, defaultValue, onChange]
  );

  return (
    <CKFilter
      defaultValue={defaultValue}
      onChange={onChange}
      identity={identity}
      key={activeRef.current}
    >
      <div className="row z-1">
        <div className="col-auto col-lg-3 col-xl-2 lg-pt-2">
          <form method="get" className="filters" id="openCloseMobileFilter">
            <input type="hidden" name="list" value="1" />

            <div className="filters__overlay"></div>

            <div className="filters__inner">
              <div className="filters__header d-lg-none">
                <div className="mx-0 row">
                  <div className="col align-self-center">
                    <h3 className="mb-0 heading sans-serif strong lg">
                      {$pt("filter_title")}
                    </h3>
                  </div>

                  <div className="col-auto pr-0">
                    <div
                      className="filters__close"
                      onClick={() => closeMobileFilters()}
                    >
                      <span data-icon-after=""></span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="filters__content">
                <CKFilter.Selected<ProductSearchFilterGroupedOption>>
                  {({ value, onUnCheck }) => (
                    <>
                      {value.length > 0 ? (
                        <CKCollapsible
                          defaultOpen={true}
                          as="div"
                          className="filters__filter d-lg-none"
                          id="selectedFiltersToggle"
                        >
                          {({ open }) => (
                            <>
                              <CKCollapsible.Button
                                as="div"
                                className="mx-0 filters__filter-heading row mx-lg-n3"
                                onClick={() => toggleSelectedFilters(open)}
                              >
                                <div className="col">
                                  <div className="heading sans-serif strong">
                                    {$pt("filter_selected_filters")}
                                  </div>
                                </div>

                                <div className="col-auto align-self-center">
                                  <span data-icon-after=""> </span>
                                </div>
                              </CKCollapsible.Button>
                              <AnimatePresence mode="sync">
                                {open ? (
                                  <motion.div
                                    transition={{
                                      duration: 0.3,
                                      ease: [0.55, 0.055, 0.675, 0.19],
                                    }}
                                    initial={{ height: 0 }}
                                    animate={{ height: "auto" }}
                                    exit={{ height: 0 }}
                                    className="overflow-hidden"
                                  >
                                    <ul className="px-3 filters__filter-options px-lg-0 ">
                                      {value.map((item, index) => (
                                        <li
                                          key={`${item.groupName}_${item.value}_${index}`}
                                          className="mt-3"
                                        >
                                          <div
                                            className="filters__option row no-gutters"
                                            data-name="materiaal"
                                            data-value="High-quality aluminum"
                                          >
                                            <div className="col text">
                                              {item.name}:{" "}
                                              <span className="text strong dark">
                                                &nbsp; {item.value}
                                              </span>
                                            </div>
                                            <button
                                              onClick={() => {
                                                onUnCheck([item]);
                                              }}
                                            >
                                              <i className="col-auto filters__option-remove">
                                                <span data-icon-after=""></span>
                                              </i>
                                            </button>
                                          </div>
                                        </li>
                                      ))}
                                      <li className="mt-4 text-right">
                                        <CKFilter.Reset<ProductSearchFilterGroupedOption>>
                                          {({ onReset }) => (
                                            <button
                                              onClick={(e) => {
                                                if (isProductLoading) {
                                                  e.preventDefault();
                                                }
                                                onReset();
                                              }}
                                              className="link"
                                            >
                                              {$pt("filter_remove_all_filters")}
                                            </button>
                                          )}
                                        </CKFilter.Reset>
                                      </li>
                                    </ul>
                                  </motion.div>
                                ) : null}
                              </AnimatePresence>
                            </>
                          )}
                        </CKCollapsible>
                      ) : null}
                    </>
                  )}
                </CKFilter.Selected>

                {filtersGrouped.map((item, index) => {
                  return (
                    <Fragment key={`${item.name}_${index}`}>
                      {useMobileFilters ? (
                        <WbFilterGroup.Mobile
                          key={`Mobile_${item.name}_${index}`}
                          {...item}
                        />
                      ) : (
                        <WbFilterGroup.Desktop
                          key={`Desktop_${item.name}_${index}`}
                          {...item}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </div>

              <div className="p-3 filters__footer d-lg-none">
                <button
                  type="submit"
                  className="btn btn--primary w-100"
                  data-icon-after=""
                  onClick={(e) => {
                    e.preventDefault();
                    closeMobileFilters();
                  }}
                >
                  {$pt("filter_show_products_button")}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="col-12 col-lg-9 col-xl-10">
          <div className="row mb-lg-3">
            <div className="col d-lg-flex align-items-center">
              <h1 className="py-2 mb-0 heading strong sans-serif mdl underlined left">
                {categoryData?.name}
              </h1>
            </div>
          </div>
          <CKFilter.Selected<ProductSearchFilterGroupedOption>>
            {({ value, onUnCheck }) => (
              <>
                {value.length > 0 ? (
                  <div className="d-none d-lg-block">
                    <div className="mb-4 selected-filters row align-items-center mx-n2 d-none d-lg-flex">
                      <div className="px-2 col-aut0">
                        <p className="mb-0 text sm light content">
                          {$pt("filter_selected_filters")}
                        </p>
                      </div>
                      {value.map((item, index) => (
                        <div
                          key={`${item.groupName}_${item.value}_${index}`}
                          className="col-auto px-2"
                        >
                          <button
                            type="button"
                            className="selected-filters__filter text light"
                            data-icon-after=""
                            onClick={(e) => {
                              if (isProductLoading) {
                                e.preventDefault();
                              }

                              onUnCheck([item]);
                            }}
                          >
                            {item.name}:{" "}
                            <span className="text strong dark">
                              {formatFilterValue(item.value, $pt)}
                            </span>
                          </button>
                        </div>
                      ))}
                      <div className="col-auto">
                        <CKFilter.Reset<ProductSearchFilterGroupedOption>>
                          {({ onReset }) => (
                            <button
                              onClick={(e) => {
                                if (isProductLoading) {
                                  e.preventDefault();
                                }
                                onReset();
                              }}
                              className="link"
                            >
                              {$pt("filter_remove_all_filters")}
                            </button>
                          )}
                        </CKFilter.Reset>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="mb-3 row d-lg-none">
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={(e) => {
                        e.preventDefault();
                        openMobileFilters();
                      }}
                      data-icon-before=""
                      data-icon-after=""
                    >
                      {$pt("filter_expand_button")}{" "}
                      {value.length > 0 ? `(${value.length})` : ""}
                    </button>
                  </div>
                  <div className="col d-flex align-items-center">
                    <span className="text light sm">
                      {totalProductCount} {$pt("filter_number_of_products")}
                    </span>
                  </div>
                </div>
              </>
            )}
          </CKFilter.Selected>
          <div
            className={`py-2 mb-3 productlist__sorting ${
              showTotalCount ? "d-flex" : ""
            }`}
          >
            {showTotalCount && (
              <div className="d-flex align-items-center pl-2 mr-auto">
                <span className="text light">
                  {totalProductCount} {$pt("filter_number_of_products")}
                </span>
              </div>
            )}
            <WvProductsSortingCard />
          </div>
          {quickMobileFilters.options.length > 0 && (
            <div className="my-3 d-lg-none">
              <h3 className="quick-mobile-filters">
                {$pt("quick_mobile_filters_title")}
              </h3>
              <WvMultiSelect
                name={quickMobileFilters.name}
                options={quickMobileFilters.options}
                selectedValues={selectedValues}
                onSelectionChange={handleSelectionChange}
              />
            </div>
          )}
          <div className="productlist__items">
            {productsPerRow &&
              productsPerRow.map((rowProducts, rowIndex) => {
                return (
                  <Fragment key={`rowProducts_${rowIndex}`}>
                    <div className="row">
                      {rowProducts.map((product, index) => (
                        <WvProductItem
                          key={`${product.sku}_${index}`}
                          product={product}
                          imagePriority={imagePriorityAt(
                            rowIndex * PRODUCTS_PER_ROW + index
                          )}
                          itemListId="product_list_page"
                          itemListName="Product List Page"
                          customer={customer}
                          outOfStock={product.stockLevel <= 0}
                          largeImage
                        />
                      ))}
                    </div>
                    {rowProducts.length === PRODUCTS_PER_ROW ? (
                      <SliceZoneC
                        key={`uspbar_${rowIndex}`}
                        slices={uspBarImageAndTextSlices}
                        components={{
                          usp_bar_image_and_text: UspBarImageAndText,
                        }}
                        defaultComponent={({ slice }) => (
                          <div>{slice.slice_type}</div>
                        )}
                      />
                    ) : null}
                  </Fragment>
                );
              })}
            {usePagination ? (
              <WvProductListPagination
                totalProducts={totalProductCount}
                currentPage={currentPage}
              />
            ) : (
              <div ref={loadRef}></div>
            )}
          </div>
          <div className="px-3 px-xl-0">
            {faqSlice && faqSlice.length > 0 ? (
              <SliceZoneC
                slices={faqSlice}
                components={{ faq_slice: FaqSection }}
                defaultComponent={({ slice }) => <div>{slice.slice_type}</div>}
              />
            ) : null}
          </div>
          {showDescriptions && (
            <>
              <div className="row mb-lg-3">
                <div className="col d-lg-flex align-items-center">
                  <h2 className="py-2 mb-0 heading strong sans-serif mdl underlined left">
                    {categoryData?.name}
                  </h2>
                </div>
              </div>
              <div
                className="content content__mb-none readmore"
                dangerouslySetInnerHTML={{
                  __html: descriptions.long || "",
                }}
              ></div>
              <div
                className="mb-4 content readmore"
                dangerouslySetInnerHTML={{
                  __html: descriptions.short || "",
                }}
              ></div>
            </>
          )}
          {richTextSlices && richTextSlices?.length ? (
            <div className="mt-4 mb-5 row mt-lg-5">
              <div className="col-12">
                <SliceZoneC
                  slices={richTextSlices}
                  components={{
                    rich_text: RichText,
                  }}
                  defaultComponent={({ slice }) => (
                    <div>{slice.slice_type}</div>
                  )}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </CKFilter>
  );
};

export default WvSearchFilter;
